import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Mail, Loader2, RefreshCw, Check, AlertTriangle,
  FileText, User, Trash2, ChevronDown, ChevronRight,
  Link, Search, XCircle, ArrowRight, Hash,
} from 'lucide-react';
import { expiryExceptionsService } from '../../services/api/expiryExceptionsService';
import type { UnmatchedExpiry, ExpiryProcessingFailure, DuplicateExpiry, ExpiryExceptionsData } from '../../types/expiry';
import { apiClient } from '../../services/api/client';
import ConfirmDialog from '../common/ConfirmDialog';
import ExceptionCalendarPicker from '../common/ExceptionCalendarPicker';
import MatchScoreIndicator from '../common/MatchScoreIndicator';
import '../confirmations/ExceptionsModal.css';

/** Extract YYYY-MM-DD from an email date string */
const extractDatePart = (dateStr: string | undefined): string | null => {
  if (!dateStr || dateStr.length < 10) return null;
  return dateStr.substring(0, 10);
};

interface ExpiryExceptionsModalProps {
  clientId: string;
  isOpen: boolean;
  selectedDate?: string; // YYYY-MM-DD — filter unmatched expiries to this maturity date
  onClose: () => void;
  onRefreshNeeded?: () => void;
}

// Normalise a date string (DD-MM-YYYY or YYYY-MM-DD) to YYYYMMDD for comparison
const normaliseDateForCompare = (dateStr: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  if (parts[0].length === 4) return `${parts[0]}${parts[1]}${parts[2]}`; // YYYY-MM-DD
  return `${parts[2]}${parts[1]}${parts[0]}`; // DD-MM-YYYY
};

const ExpiryExceptionsModal: React.FC<ExpiryExceptionsModalProps> = ({
  clientId,
  isOpen,
  selectedDate,
  onClose,
  onRefreshNeeded,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'unmatched' | 'failures' | 'duplicates'>('unmatched');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exceptionsData, setExceptionsData] = useState<ExpiryExceptionsData | null>(null);
  // Calendar email-date filter (separate from the maturity-date selectedDate prop)
  const [calendarFilterDate, setCalendarFilterDate] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && clientId) {
      loadExceptions();
    }
  }, [isOpen, clientId]);

  const loadExceptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await expiryExceptionsService.getExceptions(clientId);
      if (result.success && result.data) {
        setExceptionsData(result.data);
      } else {
        setError(result.message || t('expiryExceptions.loadError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('expiryExceptions.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleManualMatch = async (expiryId: string, tradeId: string, entityId: string): Promise<boolean> => {
    try {
      const result = await expiryExceptionsService.manualMatch(clientId, expiryId, tradeId, entityId);
      if (result.success) {
        setExceptionsData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            unmatchedExpiries: prev.unmatchedExpiries.filter(u => u.expiryId !== expiryId),
            totalCount: prev.totalCount - 1,
          };
        });
        onRefreshNeeded?.();
      }
      return result.success;
    } catch {
      return false;
    }
  };

  const handleDismissExpiry = async (expiryId: string): Promise<boolean> => {
    try {
      const result = await expiryExceptionsService.dismissExpiry(clientId, expiryId);
      if (result.success) {
        setExceptionsData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            unmatchedExpiries: prev.unmatchedExpiries.filter(u => u.expiryId !== expiryId),
            duplicateExpiries: prev.duplicateExpiries.filter(d => d.expiryId !== expiryId),
            totalCount: prev.totalCount - 1,
          };
        });
      }
      return result.success;
    } catch {
      return false;
    }
  };

  const handleRetryProcessing = async (emailId: string): Promise<boolean> => {
    try {
      const result = await expiryExceptionsService.retryProcessing(clientId, emailId);
      if (result.success) {
        setExceptionsData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            processingFailures: prev.processingFailures.filter(f => f.emailId !== emailId),
            totalCount: prev.totalCount - 1,
          };
        });
        setTimeout(() => loadExceptions(), 1000);
      }
      return result.success;
    } catch {
      return false;
    }
  };

  const handleDismissFailure = async (emailId: string): Promise<boolean> => {
    try {
      const result = await expiryExceptionsService.dismissFailure(clientId, emailId);
      if (result.success) {
        setExceptionsData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            processingFailures: prev.processingFailures.filter(f => f.emailId !== emailId),
            totalCount: prev.totalCount - 1,
          };
        });
      }
      return result.success;
    } catch {
      return false;
    }
  };

  if (!isOpen) return null;

  // Filter unmatched expiries by selected date (maturityDate may be DD-MM-YYYY or YYYY-MM-DD)
  const allUnmatched = exceptionsData?.unmatchedExpiries || [];
  const allFailures = exceptionsData?.processingFailures || [];
  const totalCount = exceptionsData?.totalCount || 0;

  // Maturity date filter (from ForwardMaturity page) — format-aware comparison
  const maturityFiltered = selectedDate
    ? allUnmatched.filter(u => normaliseDateForCompare(u.maturityDate) === normaliseDateForCompare(selectedDate))
    : allUnmatched;

  const allDuplicates = exceptionsData?.duplicateExpiries || [];

  // Calendar email-date filter (filters all tabs by source email date)
  const filteredUnmatched = calendarFilterDate
    ? maturityFiltered.filter(u => extractDatePart(u.sourceEmailDate) === calendarFilterDate)
    : maturityFiltered;
  const filteredFailures = calendarFilterDate
    ? allFailures.filter(f => extractDatePart(f.emailDate) === calendarFilterDate)
    : allFailures;
  const filteredDuplicates = calendarFilterDate
    ? allDuplicates.filter(d => extractDatePart(d.sourceEmailDate) === calendarFilterDate)
    : allDuplicates;

  const unmatchedCount = filteredUnmatched.length;
  const failuresCount = filteredFailures.length;
  const duplicatesCount = filteredDuplicates.length;

  // Compute exception email dates for the calendar (unfiltered by calendar, but filtered by maturity if active)
  const exceptionDatesSet = new Set<string>();
  for (const item of maturityFiltered) {
    const d = extractDatePart(item.sourceEmailDate);
    if (d) exceptionDatesSet.add(d);
  }
  for (const item of allFailures) {
    const d = extractDatePart(item.emailDate);
    if (d) exceptionDatesSet.add(d);
  }
  for (const item of allDuplicates) {
    const d = extractDatePart(item.sourceEmailDate);
    if (d) exceptionDatesSet.add(d);
  }
  const exceptionDates = Array.from(exceptionDatesSet);

  return (
    <div className="exceptions-modal-overlay" onClick={onClose}>
      <div className="exceptions-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="exceptions-modal-header">
          <div className="header-title-area">
            <AlertTriangle size={22} className="header-icon" />
            <h2>{t('expiryExceptions.title')}</h2>
          </div>
          <div className="header-actions">
            <ExceptionCalendarPicker
              totalCount={totalCount}
              exceptionDates={exceptionDates}
              selectedDate={calendarFilterDate}
              onDateSelect={setCalendarFilterDate}
            />
            <button className="close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="exceptions-tabs">
          <button
            className={`exceptions-tab ${activeTab === 'unmatched' ? 'active' : ''}`}
            onClick={() => setActiveTab('unmatched')}
          >
            <Mail size={18} />
            <span>{t('expiryExceptions.unmatchedTab')}</span>
            {unmatchedCount > 0 && (
              <span className="tab-badge">{unmatchedCount}</span>
            )}
          </button>
          <button
            className={`exceptions-tab ${activeTab === 'failures' ? 'active' : ''}`}
            onClick={() => setActiveTab('failures')}
          >
            <XCircle size={18} />
            <span>{t('expiryExceptions.processingFailuresTab')}</span>
            {failuresCount > 0 && (
              <span className="tab-badge error">{failuresCount}</span>
            )}
          </button>
          {duplicatesCount > 0 && (
            <button
              className={`exceptions-tab ${activeTab === 'duplicates' ? 'active' : ''}`}
              onClick={() => setActiveTab('duplicates')}
            >
              <FileText size={18} />
              <span>{t('expiryExceptions.duplicatesTab')}</span>
              <span className="tab-badge">{duplicatesCount}</span>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="exceptions-content">
          {loading ? (
            <div className="exceptions-loading">
              <Loader2 size={40} className="spinner-icon" />
              <p>{t('common.loading')}</p>
            </div>
          ) : error ? (
            <div className="exceptions-error">
              <AlertTriangle size={40} />
              <p>{error}</p>
              <button onClick={loadExceptions}>{t('common.retry')}</button>
            </div>
          ) : activeTab === 'unmatched' ? (
            <UnmatchedList
              clientId={clientId}
              items={filteredUnmatched}
              onDismiss={handleDismissExpiry}
              onManualMatch={handleManualMatch}
            />
          ) : activeTab === 'failures' ? (
            <FailuresList
              items={filteredFailures}
              onRetry={handleRetryProcessing}
              onDismiss={handleDismissFailure}
            />
          ) : (
            <DuplicatesList
              items={filteredDuplicates}
              onDismiss={handleDismissExpiry}
            />
          )}
        </div>
      </div>
    </div>
  );
};


// ── Unmatched Expiries Tab ──────────────────────────────────────────

interface UnmatchedListProps {
  clientId: string;
  items: UnmatchedExpiry[];
  onDismiss: (expiryId: string) => Promise<boolean>;
  onManualMatch: (expiryId: string, tradeId: string, entityId: string) => Promise<boolean>;
}

const UnmatchedList: React.FC<UnmatchedListProps> = ({ clientId, items, onDismiss, onManualMatch }) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div className="exceptions-empty">
        <Mail size={56} strokeWidth={1} />
        <p>{t('expiryExceptions.noUnmatched')}</p>
      </div>
    );
  }

  return (
    <div className="exceptions-list">
      {items.map((item) => (
        <UnmatchedItem
          key={item.expiryId}
          clientId={clientId}
          item={item}
          onDismiss={onDismiss}
          onManualMatch={onManualMatch}
        />
      ))}
    </div>
  );
};

const formatAmount = (amount: number | null | undefined, currency?: string) => {
  if (amount == null) return null;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
  return currency ? `${currency} ${formatted}` : formatted;
};

const UnmatchedItem: React.FC<{
  clientId: string;
  item: UnmatchedExpiry;
  onDismiss: (expiryId: string) => Promise<boolean>;
  onManualMatch: (expiryId: string, tradeId: string, entityId: string) => Promise<boolean>;
}> = ({ clientId, item, onDismiss, onManualMatch }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [showTradePicker, setShowTradePicker] = useState(false);
  const [matching, setMatching] = useState(false);

  const handleDismiss = async () => {
    setDismissing(true);
    await onDismiss(item.expiryId);
    setDismissing(false);
  };

  const handleConfirmMatch = async (tradeId: string, entityId: string) => {
    setMatching(true);
    const success = await onManualMatch(item.expiryId, tradeId, entityId);
    setMatching(false);
    if (success) setShowTradePicker(false);
  };

  const isBusy = dismissing || matching;

  return (
    <div className={`exception-card unrecognised ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="exception-card-header clickable" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <span className="expand-icon">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <div className="email-info-compact">
            <div className="email-sender">
              <User size={14} />
              <span>{item.counterpartyName}</span>
            </div>
            {!isExpanded && item.maturityDate && (
              <span className="trade-summary-badge">{item.maturityDate}</span>
            )}
            {!isExpanded && item.counterpartyTradeNumber && (
              <span className="trades-count-badge">#{item.counterpartyTradeNumber}</span>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Email Subject */}
          <div className="email-subject-row">
            <FileText size={14} />
            <span title={item.sourceEmailSubject}>{item.sourceEmailSubject}</span>
          </div>

          {/* Expiry Details */}
          <div className="extracted-trades-section">
            <div className="section-label">
              {t('expiryExceptions.expiryDetails')}
            </div>
            <div className="trade-cards-grid">
              <div className="trade-card">
                <div className="trade-card-body">
                  <div className="trade-details">
                    {item.counterpartyTradeNumber && (
                      <div className="trade-detail">
                        <span className="detail-label">{t('expiryExceptions.bankTradeNumber')}:</span>
                        <span className="detail-value">{item.counterpartyTradeNumber}</span>
                      </div>
                    )}
                    <div className="trade-detail">
                      <span className="detail-label">{t('forwardMaturity.columns.maturityDate')}:</span>
                      <span className="detail-value">{item.maturityDate}</span>
                    </div>
                    {item.settlementType && (
                      <div className="trade-detail">
                        <span className="detail-label">{t('expiryExceptions.settlementType', 'Settlement Type')}:</span>
                        <span className="detail-value">{item.settlementType}</span>
                      </div>
                    )}
                    {/* For Entrega Física: show both We Pay and We Receive.
                        For Compensación: show whichever is available. */}
                    {item.settlementType === 'Entrega Física' ? (
                      <>
                        {item.wePayAmount != null && (
                          <div className="trade-detail">
                            <span className="detail-label">{t('forwardMaturity.columns.pagamos')}:</span>
                            <span className="detail-value">{formatAmount(item.wePayAmount, item.settlementCurrency)}</span>
                          </div>
                        )}
                        {item.weReceiveAmount != null && (
                          <div className="trade-detail">
                            <span className="detail-label">{t('forwardMaturity.columns.recibimos')}:</span>
                            <span className="detail-value">{formatAmount(item.weReceiveAmount, item.settlementCurrency)}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {item.wePayAmount != null && (
                          <div className="trade-detail">
                            <span className="detail-label">{t('forwardMaturity.columns.pagamos')}:</span>
                            <span className="detail-value">{formatAmount(item.wePayAmount, item.settlementCurrency)}</span>
                          </div>
                        )}
                        {item.weReceiveAmount != null && (
                          <div className="trade-detail">
                            <span className="detail-label">{t('forwardMaturity.columns.recibimos')}:</span>
                            <span className="detail-value">{formatAmount(item.weReceiveAmount, item.settlementCurrency)}</span>
                          </div>
                        )}
                      </>
                    )}
                    <div className="trade-detail">
                      <span className="detail-label">{t('expiryExceptions.emailDate')}:</span>
                      <span className="detail-value">{item.sourceEmailDate}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trade Picker or Actions */}
          {showTradePicker ? (
            <ExpiryTradePicker
              clientId={clientId}
              maturityDate={item.maturityDate}
              cptyItem={item}
              onConfirm={handleConfirmMatch}
              onCancel={() => setShowTradePicker(false)}
              isMatching={matching}
            />
          ) : (
            <div className="exception-card-footer">
              <button
                className="dismiss-btn"
                onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                disabled={isBusy}
                title={t('expiryExceptions.dismissTooltip')}
              >
                {dismissing ? (
                  <Loader2 size={16} className="spinner-icon" />
                ) : (
                  <Trash2 size={16} />
                )}
                <span>{t('expiryExceptions.dismiss')}</span>
              </button>
              <button
                className="match-to-trade-btn"
                onClick={(e) => { e.stopPropagation(); setShowTradePicker(true); }}
                disabled={isBusy}
              >
                <Link size={16} />
                <span>{t('expiryExceptions.matchToTrade')}</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};


// ── Match score computation for expiry trade picker ─────────────────

function computeExpiryMatchScore(
  cptyItem: UnmatchedExpiry,
  trade: Record<string, unknown>,
  tf: (t: Record<string, unknown>, k: string) => string,
): { score: number; maxScore: number } {
  const cptySettlement = (cptyItem.settlementType || '').toLowerCase();
  const isPhysical = cptySettlement.includes('entrega');
  const maxScore = isPhysical ? 5 : 4;

  let score = 0;

  // 1. Counterparty name
  if (
    cptyItem.counterpartyName &&
    tf(trade, 'CounterpartyName').toLowerCase() === cptyItem.counterpartyName.toLowerCase()
  ) {
    score += 1;
  }

  // 2. Settlement type
  if (
    cptyItem.settlementType &&
    tf(trade, 'SettlementType').toLowerCase() === cptySettlement
  ) {
    score += 1;
  }

  // 3. Maturity date
  if (cptyItem.maturityDate && tf(trade, 'MaturityDate') === cptyItem.maturityDate) {
    score += 1;
  }

  // Amount matching (tolerance < 1.0)
  if (isPhysical) {
    // Entrega Física: compare WePayAmount and WeReceiveAmount separately
    if (cptyItem.wePayAmount != null) {
      const tradePay = trade.WePayAmount as number | undefined;
      if (tradePay != null && Math.abs(Math.abs(tradePay) - Math.abs(cptyItem.wePayAmount)) < 1.0) {
        score += 1;
      }
    }
    if (cptyItem.weReceiveAmount != null) {
      const tradeReceive = trade.WeReceiveAmount as number | undefined;
      if (tradeReceive != null && Math.abs(Math.abs(tradeReceive) - Math.abs(cptyItem.weReceiveAmount)) < 1.0) {
        score += 1;
      }
    }
  } else {
    // Compensación: compare settlementAmount against whichever trade amount is available
    const cptyAmt = cptyItem.settlementAmount ?? cptyItem.wePayAmount ?? cptyItem.weReceiveAmount;
    if (cptyAmt != null) {
      const tradePay = trade.WePayAmount as number | undefined;
      const tradeReceive = trade.WeReceiveAmount as number | undefined;
      const matchesPay = tradePay != null && Math.abs(Math.abs(tradePay) - Math.abs(cptyAmt)) < 1.0;
      const matchesReceive = tradeReceive != null && Math.abs(Math.abs(tradeReceive) - Math.abs(cptyAmt)) < 1.0;
      if (matchesPay || matchesReceive) {
        score += 1;
      }
    }
  }

  return { score, maxScore };
}

// ── Trade Picker (for manual matching) ──────────────────────────────

const ExpiryTradePicker: React.FC<{
  clientId: string;
  maturityDate: string;
  cptyItem: UnmatchedExpiry;
  onConfirm: (tradeId: string, entityId: string) => void;
  onCancel: () => void;
  isMatching: boolean;
}> = ({ clientId, maturityDate, cptyItem, onConfirm, onCancel, isMatching }) => {
  const { t } = useTranslation();
  const [trades, setTrades] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTradeId, setSelectedTradeId] = useState<string | null>(null);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    loadTrades();
  }, [clientId, maturityDate]);

  const loadTrades = async () => {
    setLoading(true);
    try {
      // Convert maturity date to YYYY-MM-DD for the API (handles both DD-MM-YYYY and YYYY-MM-DD)
      let apiDate = maturityDate;
      const ddmmParts = maturityDate.split('-');
      if (ddmmParts.length === 3 && ddmmParts[0].length <= 2) {
        apiDate = `${ddmmParts[2]}-${ddmmParts[1]}-${ddmmParts[0]}`;
      }
      // If already YYYY-MM-DD (parts[0].length === 4), apiDate stays as-is
      const result = await apiClient.get<Record<string, unknown>[]>(
        `/api/v1/clients/${clientId}/forward-expiries?date=${apiDate}`
      );
      if (result.success && result.data) {
        // Show trades that don't already have CounterpartySettlement
        const available = result.data.filter(t => !t.CounterpartySettlement);
        setTrades(available);
      }
    } catch {
      setTrades([]);
    } finally {
      setLoading(false);
    }
  };

  const tf = (trade: Record<string, unknown>, key: string): string => {
    const val = trade[key];
    return val != null ? String(val) : '';
  };

  // Score, sort, and filter trades
  const scoredTrades = trades.map(trade => ({
    trade,
    ...computeExpiryMatchScore(cptyItem, trade, tf),
  }));

  // Sort: counterparty match first, then descending score
  const cptyName = (cptyItem.counterpartyName || '').toLowerCase();
  scoredTrades.sort((a, b) => {
    const aIsCpty = tf(a.trade, 'CounterpartyName').toLowerCase() === cptyName ? 1 : 0;
    const bIsCpty = tf(b.trade, 'CounterpartyName').toLowerCase() === cptyName ? 1 : 0;
    if (aIsCpty !== bIsCpty) return bIsCpty - aIsCpty;
    return b.score - a.score;
  });

  const filteredTrades = scoredTrades.filter(({ trade }) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      tf(trade, 'TradeNumber').toLowerCase().includes(q) ||
      tf(trade, 'CounterpartyName').toLowerCase().includes(q) ||
      tf(trade, 'Currency1').toLowerCase().includes(q) ||
      tf(trade, 'Direction').toLowerCase().includes(q)
    );
  });

  return (
    <div className="trade-picker-panel">
      <div className="trade-picker-header">
        <span className="trade-picker-title">{t('expiryExceptions.selectTrade')}</span>
        <div className="trade-picker-search">
          <Search size={14} />
          <input
            type="text"
            placeholder={t('expiryExceptions.searchTrades')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>

      {/* Counterparty reference: what they claim */}
      {(cptyItem.wePayAmount != null || cptyItem.weReceiveAmount != null) && (
        <div className="trade-picker-cpty-reference">
          <span className="cpty-ref-label">{t('expiryExceptions.counterpartySays')}:</span>
          {cptyItem.wePayAmount != null && (
            <span className="cpty-ref-amount">
              {t('forwardMaturity.columns.pagamos')}: {formatAmount(cptyItem.wePayAmount, cptyItem.settlementCurrency)}
            </span>
          )}
          {cptyItem.weReceiveAmount != null && (
            <span className="cpty-ref-amount">
              {t('forwardMaturity.columns.recibimos')}: {formatAmount(cptyItem.weReceiveAmount, cptyItem.settlementCurrency)}
            </span>
          )}
        </div>
      )}

      <div className="trade-picker-list">
        {loading ? (
          <div className="trade-picker-loading">
            <Loader2 size={24} className="spinner-icon" />
          </div>
        ) : filteredTrades.length === 0 ? (
          <div className="trade-picker-empty">
            <p>{trades.length === 0
              ? t('expiryExceptions.noTradesForDate')
              : t('expiryExceptions.noTradesMatchSearch')
            }</p>
          </div>
        ) : (
          filteredTrades.map(({ trade, score, maxScore }) => {
            const tradeId = String(trade.id || '');
            const entityId = String(trade.entityId || '');
            const tradeNumber = tf(trade, 'TradeNumber');
            const direction = tf(trade, 'Direction');
            const currency1 = tf(trade, 'Currency1');
            const currency2 = tf(trade, 'Currency2');
            const qty1 = trade.QuantityCurrency1 as number | undefined;
            const price = Number(trade.Price || 0);
            const counterparty = tf(trade, 'CounterpartyName');
            const settlementType = tf(trade, 'SettlementType');

            const wePayAmt = trade.WePayAmount as number | undefined;
            const weReceiveAmt = trade.WeReceiveAmount as number | undefined;
            const settlCcy = tf(trade, 'SettlementCurrency') || currency2;

            // Determine if this trade's settlement amount matches counterparty's
            const cptyAmt = cptyItem.wePayAmount ?? cptyItem.weReceiveAmount;
            let amountMatch: 'match' | 'mismatch' | 'none' = 'none';
            if (cptyAmt != null && (wePayAmt != null || weReceiveAmt != null)) {
              const absCpty = Math.abs(cptyAmt);
              const matchesPay = wePayAmt != null && Math.abs(Math.abs(wePayAmt) - absCpty) < 1.0;
              const matchesReceive = weReceiveAmt != null && Math.abs(Math.abs(weReceiveAmt) - absCpty) < 1.0;
              amountMatch = (matchesPay || matchesReceive) ? 'match' : 'mismatch';
            }

            return (
              <div
                key={tradeId}
                className={`trade-picker-item ${selectedTradeId === tradeId ? 'selected' : ''} ${amountMatch === 'match' ? 'amount-match' : ''}`}
                onClick={(e) => { e.stopPropagation(); setSelectedTradeId(tradeId); setSelectedEntityId(entityId); }}
              >
                <div className="picker-trade-main">
                  <span className="picker-trade-number">#{tradeNumber}</span>
                  <span className={`picker-trade-direction ${direction.toLowerCase()}`}>
                    {direction}
                  </span>
                  <span className="picker-trade-amount">
                    {formatAmount(qty1, currency1)}
                  </span>
                  {currency2 && (
                    <>
                      <ArrowRight size={12} className="currency-arrow" />
                      <span className="picker-trade-ccy2">{currency2}</span>
                    </>
                  )}
                  {price > 0 && (
                    <span className="picker-trade-price">@ {price}</span>
                  )}
                  <MatchScoreIndicator score={score} maxScore={maxScore} />
                </div>
                <div className="picker-trade-details">
                  <span>{counterparty}</span>
                  {settlementType && <span>{settlementType}</span>}
                </div>
                {(wePayAmt != null || weReceiveAmt != null) && (
                  <div className="picker-trade-settlement">
                    {wePayAmt != null && (
                      <span className="settlement-line">
                        {t('forwardMaturity.columns.pagamos')}: {formatAmount(wePayAmt, settlCcy)}
                      </span>
                    )}
                    {weReceiveAmt != null && (
                      <span className="settlement-line">
                        {t('forwardMaturity.columns.recibimos')}: {formatAmount(weReceiveAmt, settlCcy)}
                      </span>
                    )}
                    {amountMatch === 'match' && (
                      <span className="settlement-match-badge match">
                        <Check size={12} /> {t('expiryExceptions.amountMatches')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="trade-picker-actions">
        <button
          className="dismiss-btn"
          onClick={(e) => { e.stopPropagation(); onCancel(); }}
          disabled={isMatching}
        >
          <span>{t('common.cancel')}</span>
        </button>
        <button
          className="match-confirm-btn"
          onClick={(e) => { e.stopPropagation(); setShowConfirmDialog(true); }}
          disabled={!selectedTradeId || isMatching}
        >
          {isMatching ? (
            <Loader2 size={16} className="spinner-icon" />
          ) : (
            <Check size={16} />
          )}
          <span>{t('expiryExceptions.confirmMatch')}</span>
        </button>
      </div>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={t('expiryExceptions.confirmMatchTitle')}
        message={t('expiryExceptions.confirmMatchMessage')}
        confirmLabel={t('expiryExceptions.confirmMatch')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => { setShowConfirmDialog(false); selectedTradeId && onConfirm(selectedTradeId, selectedEntityId || ''); }}
        onCancel={() => setShowConfirmDialog(false)}
        type="info"
      />
    </div>
  );
};


// ── Processing Failures Tab ─────────────────────────────────────────

interface FailuresListProps {
  items: ExpiryProcessingFailure[];
  onRetry: (emailId: string) => Promise<boolean>;
  onDismiss: (emailId: string) => Promise<boolean>;
}

const FailuresList: React.FC<FailuresListProps> = ({ items, onRetry, onDismiss }) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div className="exceptions-empty">
        <XCircle size={56} strokeWidth={1} />
        <p>{t('expiryExceptions.noProcessingFailures')}</p>
      </div>
    );
  }

  return (
    <div className="exceptions-list">
      {items.map((item) => (
        <FailureItem key={item.emailId} item={item} onRetry={onRetry} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

const FailureItem: React.FC<{
  item: ExpiryProcessingFailure;
  onRetry: (emailId: string) => Promise<boolean>;
  onDismiss: (emailId: string) => Promise<boolean>;
}> = ({ item, onRetry, onDismiss }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [retryResult, setRetryResult] = useState<'success' | 'failed' | null>(null);

  const handleRetry = async () => {
    setRetrying(true);
    setRetryResult(null);
    const success = await onRetry(item.emailId);
    setRetrying(false);
    setRetryResult(success ? 'success' : 'failed');
  };

  const handleDismiss = async () => {
    setDismissing(true);
    await onDismiss(item.emailId);
    setDismissing(false);
  };

  const isBusy = retrying || dismissing;

  const [showTechDetails, setShowTechDetails] = useState(false);

  const statusLabel = item.processingStatus === 'llm_failed'
    ? t('expiryExceptions.statusLlmFailed')
    : item.processingStatus === 'no_client'
    ? t('expiryExceptions.statusNoClient')
    : item.processingStatus === 'not_settlement'
    ? t('expiryExceptions.statusNotSettlement', 'Not Settlement')
    : t('expiryExceptions.statusProcessingFailed');

  // User-friendly error message based on processing status
  // "Review below" suffix is only appended when the email body is actually available
  const reviewBelowSuffix = item.emailBody
    ? ' ' + t('expiryExceptions.friendlyError.reviewBelow', 'You can review the email content below.')
    : '';
  const friendlyMessage = item.processingStatus === 'not_settlement'
    ? t('expiryExceptions.friendlyError.notSettlement', 'This email does not appear to be a settlement/expiry notification and has been skipped.') + reviewBelowSuffix
    : item.processingStatus === 'llm_failed'
    ? t('expiryExceptions.friendlyError.llmFailed', 'This email could not be processed. It may not contain expiry information, or the format was not recognised.') + reviewBelowSuffix
    : item.processingStatus === 'no_client'
    ? t('expiryExceptions.friendlyError.noClient', 'This email could not be linked to a client account. The sender may not be associated with any configured counterparty.')
    : t('expiryExceptions.friendlyError.processingFailed', 'An unexpected error occurred while processing this email. You can retry processing or dismiss it.');

  // Format email date as DD/MM/YYYY
  const formatEmailDate = (dateStr: string): string => {
    if (!dateStr) return '';
    // If already DD/MM/YYYY, return as-is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
    // If YYYY-MM-DD format, convert
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    return dateStr;
  };

  const isNotSettlement = item.processingStatus === 'not_settlement';

  return (
    <div className={`exception-card processing-failure ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="exception-card-header clickable" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <span className="expand-icon">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <div className="email-info-compact">
            <div className="email-sender">
              <User size={14} />
              <span>{item.senderEmail || t('expiryExceptions.unknownSender')}</span>
            </div>
            {!isExpanded && item.emailDate && (
              <span className="trades-count-badge">{formatEmailDate(item.emailDate)}</span>
            )}
            {!isExpanded && (
              <span className="trade-summary-badge">{statusLabel}</span>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <>
          <div className="email-subject-row">
            <FileText size={14} />
            <span title={item.subject}>{item.subject}</span>
          </div>

          <div className="processing-error-section">
            {/* Friendly error message */}
            <div className="friendly-error-message">
              {friendlyMessage}
            </div>

            <div className="error-detail-row">
              <span className="detail-label">{t('expiryExceptions.status')}:</span>
              <span className="detail-value">{statusLabel}</span>
            </div>
            {item.emailDate && (
              <div className="error-detail-row">
                <span className="detail-label">{t('expiryExceptions.emailDate')}:</span>
                <span className="detail-value">{formatEmailDate(item.emailDate)}</span>
              </div>
            )}

            {/* Email body content */}
            {item.emailBody && (
              <div className="email-body-section">
                <div className="detail-label">{t('expiryExceptions.emailContent', 'Email Content')}:</div>
                <div className="email-body-content">{item.emailBody}</div>
              </div>
            )}

            {/* Technical details (collapsible) */}
            {item.processingError && !isNotSettlement && (
              <div className="tech-details-section">
                <button
                  type="button"
                  className="tech-details-toggle"
                  onClick={(e) => { e.stopPropagation(); setShowTechDetails(!showTechDetails); }}
                >
                  {showTechDetails ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span>{t('expiryExceptions.technicalDetails', 'Technical details')}</span>
                </button>
                {showTechDetails && (
                  <div className="tech-details-content">
                    <code>{item.processingError}</code>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="exception-card-footer">
            {retryResult ? (
              <div className={`reprocess-result-banner ${retryResult === 'success' ? 'success' : 'no-match'}`}>
                {retryResult === 'success' ? (
                  <>
                    <Check size={18} />
                    <span>{t('expiryExceptions.retrySuccess')}</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle size={18} />
                    <span>{t('expiryExceptions.retryFailed')}</span>
                  </>
                )}
              </div>
            ) : (
              <>
                <button
                  className="dismiss-btn"
                  onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                  disabled={isBusy}
                  title={t('expiryExceptions.dismissTooltip')}
                >
                  {dismissing ? (
                    <Loader2 size={16} className="spinner-icon" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                  <span>{t('expiryExceptions.dismiss')}</span>
                </button>
                {!isNotSettlement && (
                  <button
                    className="reprocess-btn"
                    onClick={(e) => { e.stopPropagation(); handleRetry(); }}
                    disabled={isBusy}
                  >
                    {retrying ? (
                      <Loader2 size={16} className="spinner-icon" />
                    ) : (
                      <RefreshCw size={16} />
                    )}
                    <span>{t('expiryExceptions.retryProcessing')}</span>
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ── Duplicate Expiries Tab ──────────────────────────────────────────

interface DuplicatesListProps {
  items: DuplicateExpiry[];
  onDismiss: (expiryId: string) => Promise<boolean>;
}

const DuplicatesList: React.FC<DuplicatesListProps> = ({ items, onDismiss }) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div className="exceptions-empty">
        <FileText size={56} strokeWidth={1} />
        <p>{t('expiryExceptions.noDuplicates')}</p>
      </div>
    );
  }

  return (
    <div className="exceptions-list">
      {items.map((item) => (
        <DuplicateItem key={item.expiryId} item={item} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

const DuplicateItem: React.FC<{
  item: DuplicateExpiry;
  onDismiss: (expiryId: string) => Promise<boolean>;
}> = ({ item, onDismiss }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const handleDismiss = async () => {
    setDismissing(true);
    await onDismiss(item.expiryId);
    setDismissing(false);
  };

  const fmt = (amount: number | null) => {
    if (amount == null) return '—';
    return amount.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const stateLabel = item.duplicateExpiryState === 'agreed'
    ? t('expiryExceptions.stateAgreed')
    : t('expiryExceptions.stateFinal');

  return (
    <div className={`exception-card ${expanded ? 'expanded' : ''}`}>
      <div className="exception-header" onClick={() => setExpanded(!expanded)}>
        <div className="exception-header-left">
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <div className="exception-summary">
            <span className="exception-title">
              {item.counterpartyName} — {t('expiryExceptions.trade')} {item.duplicateTradeNumber}
            </span>
            <span className="exception-subtitle">
              {item.maturityDate} · {t('expiryExceptions.duplicateState', { state: stateLabel })}
            </span>
          </div>
        </div>
        <div className="exception-header-right">
          <span className="exception-date">
            {item.sourceEmailDate ? extractDatePart(item.sourceEmailDate) : ''}
          </span>
        </div>
      </div>

      {expanded && (
        <>
          <div className="exception-details">
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">{t('expiryExceptions.tradeNumber')}:</span>
                <span className="detail-value">{item.duplicateTradeNumber}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t('expiryExceptions.currentState')}:</span>
                <span className="detail-value">{stateLabel}</span>
              </div>
              {item.wePayAmount != null && (
                <div className="detail-item">
                  <span className="detail-label">{t('expiryExceptions.wePay')}:</span>
                  <span className="detail-value">{item.wePayCurrency} {fmt(item.wePayAmount)}</span>
                </div>
              )}
              {item.weReceiveAmount != null && (
                <div className="detail-item">
                  <span className="detail-label">{t('expiryExceptions.weReceive')}:</span>
                  <span className="detail-value">{item.weReceiveCurrency} {fmt(item.weReceiveAmount)}</span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">{t('expiryExceptions.emailSubject')}:</span>
                <span className="detail-value">{item.sourceEmailSubject}</span>
              </div>
            </div>
          </div>
          <div className="exception-actions">
            <button
              className="dismiss-btn"
              onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
              disabled={dismissing}
              title={t('expiryExceptions.dismissTooltip')}
            >
              {dismissing ? (
                <Loader2 size={16} className="spinner-icon" />
              ) : (
                <Trash2 size={16} />
              )}
              <span>{t('expiryExceptions.dismiss')}</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ExpiryExceptionsModal;
